import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';

import Box from '../box/box.jsx';
import SpriteInfo from '../../containers/sprite-info.jsx';
import SpriteList from './sprite-list.jsx';
import ActionMenu from '../action-menu/action-menu.jsx';
import SpriteCreator from '../ai-sprite-creator/spriteCreator.jsx';
import {STAGE_DISPLAY_SIZES} from '../../lib/layout-constants';
import {isRtl} from 'scratch-l10n';

import styles from './sprite-selector.css';

import aiIcon from '../action-menu/icon--ai.svg';
import fileUploadIcon from '../action-menu/icon--file-upload.svg';
import paintIcon from '../action-menu/icon--paint.svg';
import spriteIcon from '../action-menu/icon--sprite.svg';
import surpriseIcon from '../action-menu/icon--surprise.svg';
import searchIcon from '../action-menu/icon--search.svg';

const messages = defineMessages({
    addSpriteFromLibrary: {
        id: 'gui.spriteSelector.addSpriteFromLibrary',
        description: 'Button to add a sprite in the target pane from library',
        defaultMessage: 'Choose a Character'
    },
    addSpriteFromAI: {
        id: 'gui.spriteSelector.addSpriteFromAI',
        description: 'Button to create a sprite with AI',
        defaultMessage: 'Create Character with AI'
    },
    addSpriteFromPaint: {
        id: 'gui.spriteSelector.addSpriteFromPaint',
        description: 'Button to add a sprite in the target pane from paint',
        defaultMessage: 'Paint'
    },
    addSpriteFromSurprise: {
        id: 'gui.spriteSelector.addSpriteFromSurprise',
        description: 'Button to add a random sprite in the target pane',
        defaultMessage: 'Surprise'
    },
    addSpriteFromFile: {
        id: 'gui.spriteSelector.addSpriteFromFile',
        description: 'Button to add a sprite in the target pane from file',
        defaultMessage: 'Upload Character'
    }
});

const SpriteSelectorComponent = function (props) {
    const [showAISpriteCreator, setShowAISpriteCreator] = useState(false);

    const {
        editingTarget,
        hoveredTarget,
        onAISpriteAdd,
        onChangeSpriteDirection,
        onChangeSpriteName,
        onChangeSpriteRotationStyle,
        onChangeSpriteSize,
        onChangeSpriteVisibility,
        onChangeSpriteX,
        onChangeSpriteY,
        onDrop,
        onDeleteSprite,
        onDuplicateSprite,
        onExportSprite,
        onFileUploadClick,
        onNewSpriteClick,
        onPaintSpriteClick,
        onSelectSprite,
        onSpriteUpload,
        onSurpriseSpriteClick,
        raised,
        selectedId,
        spriteFileInput,
        sprites,
        stageSize,
        ...componentProps
    } = props;

    const intl = useIntl();

    let selectedSprite = sprites[selectedId];
    let spriteInfoDisabled = false;

    if (typeof selectedSprite === 'undefined') {
        selectedSprite = {};
        spriteInfoDisabled = true;
    }

    return (
        <Box
            className={styles.spriteSelector}
            {...componentProps}
        >
            <SpriteInfo
                direction={selectedSprite.direction}
                disabled={spriteInfoDisabled}
                name={selectedSprite.name}
                rotationStyle={selectedSprite.rotationStyle}
                size={selectedSprite.size}
                stageSize={stageSize}
                visible={selectedSprite.visible}
                x={selectedSprite.x}
                y={selectedSprite.y}
                onChangeDirection={onChangeSpriteDirection}
                onChangeName={onChangeSpriteName}
                onChangeRotationStyle={onChangeSpriteRotationStyle}
                onChangeSize={onChangeSpriteSize}
                onChangeVisibility={onChangeSpriteVisibility}
                onChangeX={onChangeSpriteX}
                onChangeY={onChangeSpriteY}
            />

            <SpriteList
                editingTarget={editingTarget}
                hoveredTarget={hoveredTarget}
                items={Object.keys(sprites).map(id => sprites[id])}
                raised={raised}
                selectedId={selectedId}
                onDeleteSprite={onDeleteSprite}
                onDrop={onDrop}
                onDuplicateSprite={onDuplicateSprite}
                onExportSprite={onExportSprite}
                onSelectSprite={onSelectSprite}
            />

       <ActionMenu
    className={styles.addButton}
    img={spriteIcon}
    moreButtons={[
        {
            title: intl.formatMessage(messages.addSpriteFromAI),
            img: aiIcon,
            onClick: () => setShowAISpriteCreator(true)
        },
        {
            title: intl.formatMessage(messages.addSpriteFromLibrary),
            img: searchIcon,
            onClick: onNewSpriteClick
        },
        {
            title: intl.formatMessage(messages.addSpriteFromFile),
            img: fileUploadIcon,
            onClick: onFileUploadClick,
            fileAccept: '.svg, .png, .bmp, .jpg, .jpeg, .sprite2, .sprite3, .gif',
            fileChange: onSpriteUpload,
            fileInput: spriteFileInput,
            fileMultiple: true
        },
        {
            title: intl.formatMessage(messages.addSpriteFromSurprise),
            img: surpriseIcon,
            onClick: onSurpriseSpriteClick
        },
        {
            title: intl.formatMessage(messages.addSpriteFromPaint),
            img: paintIcon,
            onClick: onPaintSpriteClick
        }
    ]}
    title="Sprites"
    tooltipPlace={isRtl(intl.locale) ? 'right' : 'left'}
    onClick={() => {}}
/>

            {showAISpriteCreator && (
                <div
                    style={{
                        position: 'fixed',
                        zIndex: 1000,
                        top: '80px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                        width: 'calc(100% - 80px)',
                        maxWidth: '850px',
                        maxHeight: 'calc(100vh - 110px)',
                        overflowY: 'auto'
                    }}
                >
                    <SpriteCreator
                        onAddToProject={imageBase64 => {
                            console.log(
                                'AI sprite: sending to Scratch importer',
                                imageBase64.length
                            );

                            onAISpriteAdd(imageBase64);
                            setShowAISpriteCreator(false);
                        }}
                        onClose={() => setShowAISpriteCreator(false)}
                    />
                </div>
            )}
        </Box>
    );
};

SpriteSelectorComponent.propTypes = {
    editingTarget: PropTypes.string,
    hoveredTarget: PropTypes.shape({
        hoveredSprite: PropTypes.string,
        receivedBlocks: PropTypes.bool
    }),
    onAISpriteAdd: PropTypes.func.isRequired,
    onChangeSpriteDirection: PropTypes.func,
    onChangeSpriteName: PropTypes.func,
    onChangeSpriteRotationStyle: PropTypes.func,
    onChangeSpriteSize: PropTypes.func,
    onChangeSpriteVisibility: PropTypes.func,
    onChangeSpriteX: PropTypes.func,
    onChangeSpriteY: PropTypes.func,
    onDeleteSprite: PropTypes.func,
    onDrop: PropTypes.func,
    onDuplicateSprite: PropTypes.func,
    onExportSprite: PropTypes.func,
    onFileUploadClick: PropTypes.func,
    onNewSpriteClick: PropTypes.func,
    onPaintSpriteClick: PropTypes.func,
    onSelectSprite: PropTypes.func,
    onSpriteUpload: PropTypes.func,
    onSurpriseSpriteClick: PropTypes.func,
    raised: PropTypes.bool,
    selectedId: PropTypes.string,
    spriteFileInput: PropTypes.func,
    sprites: PropTypes.shape({
        id: PropTypes.shape({
            costume: PropTypes.shape({
                url: PropTypes.string,
                name: PropTypes.string.isRequired,
                bitmapResolution: PropTypes.number.isRequired,
                rotationCenterX: PropTypes.number.isRequired,
                rotationCenterY: PropTypes.number.isRequired
            }),
            name: PropTypes.string.isRequired,
            order: PropTypes.number.isRequired
        })
    }),
    stageSize: PropTypes.oneOf(Object.keys(STAGE_DISPLAY_SIZES)).isRequired
};

export default SpriteSelectorComponent;